import React, { useEffect, useState, useCallback } from "react";
import api from "../services/api";
import { FiCheckCircle, FiTrash } from "react-icons/fi";

export default function AdminBugReportsPage() {
  const [reports, setReports] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(""); // '', 'pending', 'resolved'
  const [page, setPage] = useState(1);
  const [perPage] = useState(30);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");

  // Wrap fetchReports in useCallback with all used deps
  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        ...(status ? { status } : {}),
        ...(search ? { search } : {}),
        page,
        per_page: perPage,
      }).toString();
      const res = await api.get(`/admin/bug-reports?${params}`);
      setReports(res.data.rows || []);
      setTotal(res.data.total || 0);
      setError("");
    } catch (err) {
      setError("Failed to fetch bug reports.");
    }
    setLoading(false);
  }, [status, search, page, perPage]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]); // ESLint happy: all used dependencies included

  const markResolved = async (id) => {
    setActionLoading(true);
    try {
      await api.patch(`/admin/bug-reports/${id}/resolve`);
      fetchReports();
    } catch {
      alert("Failed to mark as resolved.");
    }
    setActionLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this bug report? This cannot be undone.")) return;
    setActionLoading(true);
    try {
      await api.delete(`/admin/bug-reports/${id}`);
      fetchReports();
    } catch {
      alert("Failed to delete bug report.");
    }
    setActionLoading(false);
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="admin-container">
      <div className="admin-header" style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 14 }}>
        <h2>Bug Reports</h2>
        <input
          value={search}
          onChange={e => { setPage(1); setSearch(e.target.value); }}
          placeholder="Search bug reports…"
          style={{ flex: 1, maxWidth: 230, marginLeft: 12 }}
        />
        <select value={status} onChange={e => { setPage(1); setStatus(e.target.value); }} style={{ marginLeft: 10 }}>
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="resolved">Resolved</option>
        </select>
      </div>
      {error && <div className="error-message">{error}</div>}

      <div style={{ minHeight: 240 }}>
        {loading ? <div>Loading…</div> : (
          <table className="admin-table" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Description</th>
                <th>Page</th>
                <th>Status</th>
                <th>Timestamp</th>
                <th style={{ minWidth: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.length === 0 ? (
                <tr><td colSpan={6}>No bug reports found.</td></tr>
              ) : reports.map(r => (
                <tr key={r.id}>
                  <td>{r.id}</td>
                  <td style={{ maxWidth: 300, wordBreak: "break-word" }}>{r.description}</td>
                  <td>{r.page}</td>
                  <td>
                    {r.status === "resolved"
                      ? <span style={{ color: "#44b67a", fontWeight: 600 }}>Resolved</span>
                      : "Pending"}
                  </td>
                  <td>{r.timestamp ? new Date(r.timestamp).toLocaleString() : ""}</td>
                  <td>
                    {r.status !== "resolved" && (
                      <button
                        className="link-btn"
                        style={{ color: "#44b67a" }}
                        title="Mark as resolved"
                        onClick={() => markResolved(r.id)}
                        disabled={actionLoading}
                      >
                        <FiCheckCircle /> Resolve
                      </button>
                    )}
                    <button
                      className="link-btn"
                      style={{ color: "#c23" }}
                      title="Delete"
                      onClick={() => handleDelete(r.id)}
                      disabled={actionLoading}
                    >
                      <FiTrash /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div style={{ display: "flex", justifyContent: "center", margin: 18 }}>
        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i}
            className={`page-btn${page === i + 1 ? " active" : ""}`}
            style={{
              margin: "0 2px",
              border: "1px solid #ddd",
              borderRadius: 5,
              padding: "4px 11px",
              fontWeight: 600,
              background: page === i + 1 ? "#eaf6ff" : "#fff",
              color: page === i + 1 ? "#297" : "#333",
            }}
            onClick={() => setPage(i + 1)}
            disabled={page === i + 1}
          >
            {i + 1}
          </button>
        ))}
      </div>
    </div>
  );
}
